import { useRef, useState } from "react";
import { hasFullAccess } from "@shared/access";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { Group, Expense, SafeUser, RecurringExpense } from "@shared/schema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UsersRound, Users2, TrendingDown, TrendingUp, MailPlus, Check, X, Repeat, Trash2, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "wouter";
import { useAuth } from "@/lib/auth";
import { calculateGroupBalances } from "@/lib/simplify";
import { computeMyNetBalances } from "@/lib/my-balances";
import { displayBalance, AMOUNT_IN_CLASS, AMOUNT_OUT_CLASS } from "@/lib/balance-display";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CelebrationBanner } from "@/components/CelebrationBanner";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import { formatMoney } from "@/components/CurrencySelector";
import { QuickAddBar, type QuickAddBarHandle } from "@/components/QuickAddBar";
import { WhatsNewQuickAdd } from "@/components/WhatsNewQuickAdd";
import { FitText } from "@/components/FitText";
import { WhatChanged } from "@/components/WhatChanged";

// Warm, on-brand avatar colour derived from a person's id — ignores any stale
// teal/blue values still stored on legacy accounts from the old theme, so
// avatars are always in the cream/terracotta palette. Deterministic per person.
const WARM_AVATARS = ["#7A3E32", "#8C5A3C", "#9A4A2A", "#A6674A", "#8A6A32", "#B04A34", "#6B4A3A", "#B5794A"];
function warmAvatar(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return WARM_AVATARS[h % WARM_AVATARS.length];
}

function StatCard({ label, value, href, variant = "count", tone }: { label: string; value: string; href?: string; variant?: "count" | "money"; tone?: "in" | "out" }) {
  const money = variant === "money";
  // On these dark ink cards: money-in ("You're Owed") is the prominent cream
  // (ink-equivalent on a dark tile), money-out ("You Owe") is rust red.
  const moneyColor = tone === "out" ? "text-destructive" : "text-background";
  const inner = (
    <div className={`rounded-2xl p-5 border ${money ? "bg-foreground border-foreground" : `bg-card border-border ${href ? "hover-elevate cursor-pointer" : ""}`}`}>
      <p className={`text-[13px] font-medium ${money ? "text-background/70" : "text-muted-foreground"}`}>{label}</p>
      {money ? (
        // Balances can be long ($12,345.67) and these cards are half-width —
        // FitText keeps 28px when it fits and shrinks only when it doesn't.
        <FitText
          max={28}
          syncGroup="dashboard-balances"
          className={`mt-2 leading-none font-mono tabular-nums ${moneyColor}`}
          data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
        >{value}</FitText>
      ) : (
        <p
          className="mt-2 text-[32px] leading-none font-serif text-foreground"
          data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
        >{value}</p>
      )}
    </div>
  );
  if (href) return <Link href={href}>{inner}</Link>;
  return inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const userCurrency = user?.defaultCurrency;
  const quickAddRef = useRef<QuickAddBarHandle>(null);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { data: groups = [] } = useQuery<Group[]>({ queryKey: ["/api/groups"] });
  const { data: expenses = [] } = useQuery<Expense[]>({ queryKey: ["/api/expenses"] });
  const { data: friendsList = [] } = useQuery<SafeUser[]>({ queryKey: ["/api/friends"] });

  // Recurring expenses (premium only — skip fetch if not premium)
  const { data: recurringList = [] } = useQuery<RecurringExpense[]>({
    queryKey: ["/api/recurring"],
    enabled: hasFullAccess(user),
  });

  const cancelRecurringMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/recurring/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/recurring"] });
      toast({ title: "Recurring expense cancelled" });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not cancel.", variant: "destructive" });
    },
  });

  // Incoming group invites
  const { data: incomingInvites = [] } = useQuery<any[]>({
    queryKey: ["/api/invites/incoming"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/invites/incoming");
      return res.json();
    },
  });

  const acceptInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest("POST", `/api/invites/${inviteId}/accept`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invites/incoming"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups"] });
      toast({ title: "Invite accepted", description: "You've joined the group!" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const declineInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest("POST", `/api/invites/${inviteId}/decline`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invites/incoming"] });
      toast({ title: "Invite declined" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  // Calculate what the current user owes / is owed (net balance is always the same regardless of simplify).
  // displayBalance() snaps sub-$0.05 rounding residuals to 0 so the stat cards
  // don't show "You're Owed $0.01" after the user has settled everything.
  const balances = calculateGroupBalances(expenses);
  const myBalance = balances.find((b) => b.personId === user?.id);
  const rawAmount = myBalance ? myBalance.amount : 0;
  const youOwe = rawAmount < 0 ? Math.abs(displayBalance(rawAmount)) : 0;
  const youAreOwed = rawAmount > 0 ? displayBalance(rawAmount) : 0;

  // Per-group settlements: use simplified or pairwise based on each group's setting
  // (Shared with the quick-add pill + Voice Mode via computeMyNetBalances.)
  const mySettlements = user?.id
    ? computeMyNetBalances(expenses, groups, user.id).map((b) => b.amount > 0
      ? { from: b.personId, to: user.id, amount: b.amount }
      : { from: user.id, to: b.personId, amount: -b.amount })
    : [];

  // Batch-fetch all group members in one request (avoids N+1)
  const { data: groupMembersData } = useQuery<SafeUser[]>({
    queryKey: ["/api/members/all"],
    enabled: groups.length > 0,
  });

  // Combine group members + friends for name lookups, dedup by id
  const allMembersData = (() => {
    const combined = [...(groupMembersData || []), ...friendsList];
    const seen = new Set<string>();
    return combined.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  })();

  const allMembers = allMembersData;
  const getPersonName = (id: string) => {
    if (id === user?.id) return "You";
    return allMembers.find((m) => m.id === id)?.name || "Someone";
  };

  const getRecurringSplitLabel = (rec: RecurringExpense): string => {
    if (rec.groupId) {
      const grp = groups.find(g => g.id === rec.groupId);
      return grp ? `in ${grp.name}` : "in a group";
    }
    const others = rec.splitAmongIds.filter(id => id !== user?.id);
    if (others.length === 0) return "";
    if (others.length === 1) return `with ${getPersonName(others[0])}`;
    if (others.length === 2) return `with ${getPersonName(others[0])} & ${getPersonName(others[1])}`;
    return `with ${others.length} people`;
  };

  return (
    <div className="space-y-7">
      {/* One-time "what's new" feature carousel — shown once per user, ever. */}
      <WhatsNewModal />

      {/* Milestone / promo banner — only renders if user has an active campaign */}
      <CelebrationBanner />

      {/* Incoming Group Invites — shown at top if any */}
      {incomingInvites.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MailPlus className="w-4 h-4 text-primary" />
            <h2 className="text-base font-semibold font-serif">Group Invites</h2>
          </div>
          {incomingInvites.map((invite: any) => (
            <Card
              key={invite.id}
              className="p-3 border-primary/30 bg-primary/5"
              data-testid={`incoming-invite-${invite.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium break-words">
                    <span className="text-primary">{invite.inviterName}</span>
                    {" invited you to "}
                    <span className="font-semibold">{invite.groupName}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 px-2 text-xs"
                    onClick={() => acceptInviteMutation.mutate(invite.id)}
                    disabled={acceptInviteMutation.isPending || declineInviteMutation.isPending}
                    data-testid={`accept-invite-${invite.id}`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => declineInviteMutation.mutate(invite.id)}
                    disabled={acceptInviteMutation.isPending || declineInviteMutation.isPending}
                    data-testid={`decline-invite-${invite.id}`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div>
        <h1 className="text-4xl font-serif tracking-tight leading-none break-words">
          Hey, <em className="italic text-accent-foreground">{user?.name?.split(" ")[0] || "there"}</em>
        </h1>
        <p className="text-[15px] text-muted-foreground mt-2">Here's where everyone stands.</p>
      </div>

      {/* Overview — money cards weighted (dark) so the balance you care about
          is the focal point; Friends/Groups stay quiet tappable nav tiles. */}
      <div>
        <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-3">Overview</p>
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="You're Owed" value={formatMoney(youAreOwed, userCurrency)} variant="money" tone="in" />
          <StatCard label="You Owe" value={formatMoney(youOwe, userCurrency)} variant="money" tone="out" />
          <StatCard label="Friends" value={String(friendsList.length)} href="/friends" />
          <StatCard label="Groups" value={String(groups.length)} href="/groups" />
        </div>

        {/* What's new tile — grouped tightly under the overview cards. Opens a
            short tour of the quick-add pill (it used to open AI Mode). */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setWhatsNewOpen(true)}
            className="w-full group rounded-2xl border border-[#E4D8C6] bg-gradient-to-b from-[#F6EFE4] to-[#F1E7D8] p-5 text-left transition-all active:scale-[0.99] hover:border-foreground/20"
            data-testid="dashboard-whats-new-tile"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-foreground flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-background" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-serif">What's new</h3>
                  <span className="text-[9px] uppercase tracking-wider font-mono font-semibold text-background bg-accent-foreground px-1.5 py-0.5 rounded">
                    New
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Tell Spliiit anything. Type it, say it, or talk it through.
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </button>
        </div>
      </div>

      <WhatsNewQuickAdd
        open={whatsNewOpen}
        onOpenChange={setWhatsNewOpen}
        onTryIt={() => quickAddRef.current?.focus()}
      />

      {/* Your settlements — two-column rows: person left, amount right (tabular). */}
      {mySettlements.length > 0 && (
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-3">Your Balances</p>
          <div>
            {mySettlements.map((s, i) => {
              const theyOweMe = s.to === user?.id;
              const otherId = theyOweMe ? s.from : s.to;
              const other = allMembers.find((m) => m.id === otherId);
              const initial = (other?.name || "?").charAt(0).toUpperCase();
              return (
                <div key={i} className="flex items-center justify-between gap-3 py-4 border-b border-border last:border-b-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-serif shrink-0"
                      style={{ backgroundColor: warmAvatar(otherId) }}
                    >{initial}</div>
                    <div className="min-w-0">
                      <p className="text-[15px] font-medium truncate">{getPersonName(otherId)}</p>
                      <p className="text-xs text-muted-foreground">{theyOweMe ? "pays you" : "you pay"}</p>
                    </div>
                  </div>
                  <span className={`font-mono tabular-nums text-base font-semibold shrink-0 ${theyOweMe ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS}`}>{formatMoney(s.amount, userCurrency)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recurring expenses (premium) */}
      {hasFullAccess(user) && recurringList.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Repeat className="w-4 h-4 text-primary" />
            <h2 className="text-base font-semibold font-serif">Recurring</h2>
          </div>
          <div className="space-y-2">
            {recurringList.map((rec) => (
              <Card key={rec.id} className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{rec.description}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {formatMoney(rec.amount, userCurrency)} · {rec.frequency}
                    {getRecurringSplitLabel(rec) && ` · ${getRecurringSplitLabel(rec)}`}
                  </p>
                  <p className="text-xs text-muted-foreground/60 font-mono">next {rec.nextRunDate}</p>
                </div>
                <button
                  onClick={() => cancelRecurringMutation.mutate(rec.id)}
                  disabled={cancelRecurringMutation.isPending}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1"
                  title="Cancel recurring expense"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* What changed — adds, edits, deletes, settle-ups that involve you.
          Replaces the old "Recent Expenses" list (and the Expenses tab). */}
      <WhatChanged />

      {/* No empty-state card on Dashboard.
          Rationale: users who skipped the first-run wizard explicitly told us
          they didn't want to be walked through. Re-showing the same "Create
          your first group" CTA on Dashboard is a second nag — and the 4 stat
          cards above are already tappable nav (Friends 0 → /friends,
          Groups 0 → /groups). Trust the user's choice. */}

      {/* Quick add — floats above the bottom nav. AI Mode is reached from the
          bar's hint card. The spacer keeps the last row clear of it. */}
      <div aria-hidden className="h-20" />
      <QuickAddBar
        ref={quickAddRef}
        friends={friendsList}
        groups={groups}
        people={allMembers}
        balances={mySettlements.map((s) => s.to === user?.id
          ? { personId: s.from, amount: s.amount }
          : { personId: s.to, amount: -s.amount })}
        avatarColor={(id) => id === user?.id ? "#292624" : warmAvatar(id)}
      />
    </div>
  );
}
